/**
 * JSON to PDB converter
 * Converts JSON format with atomic coordinates back to PDB format
 * Based on model_output_to_pdb.py
 */

// One-letter to three-letter amino acid code mapping
const ONE_TO_THREE: Record<string, string> = {
  'A': 'ALA', 'R': 'ARG', 'N': 'ASN', 'D': 'ASP', 'C': 'CYS',
  'Q': 'GLN', 'E': 'GLU', 'G': 'GLY', 'H': 'HIS', 'I': 'ILE',
  'L': 'LEU', 'K': 'LYS', 'M': 'MET', 'F': 'PHE', 'P': 'PRO',
  'S': 'SER', 'T': 'THR', 'W': 'TRP', 'Y': 'TYR', 'V': 'VAL',
  'U': 'SEC', 'O': 'PYL', 'B': 'ASX', 'Z': 'GLX', 'J': 'XLE',
  'X': 'UNK', '_': 'UNK'  // Masked positions become unknown
};

// Define amino acid atoms in expected order
const ATOMS: Record<string, string[]> = {
  'V': ['N', 'CA', 'C', 'CB', 'O', 'CG1', 'CG2'],
  'R': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD', 'NE', 'CZ', 'NH1', 'NH2'],
  'C': ['N', 'CA', 'C', 'CB', 'O', 'SG'],
  'S': ['N', 'CA', 'C', 'CB', 'O', 'OG'],
  'L': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD1', 'CD2'],
  'Q': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD', 'OE1', 'NE2'],
  'E': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD', 'OE1', 'OE2'],
  'T': ['N', 'CA', 'C', 'CB', 'O', 'OG1', 'CG2'],
  'A': ['N', 'CA', 'C', 'CB', 'O'],
  'D': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'OD1', 'OD2'],
  'G': ['N', 'CA', 'C', 'CB', 'O'],
  'H': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'ND1', 'CD2', 'CE1', 'NE2'],
  'I': ['N', 'CA', 'C', 'CB', 'O', 'CG1', 'CG2', 'CD1'],
  'P': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD'],
  'F': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD1', 'CD2', 'CE1', 'CE2', 'CZ'],
  'K': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD', 'CE', 'NZ'],
  'N': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'OD1', 'ND2'],
  'M': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'SD', 'CE'],
  'Y': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD1', 'CD2', 'CE1', 'CE2', 'CZ', 'OH'],
  'W': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD1', 'CD2', 'NE1', 'CE2', 'CE3', 'CZ2', 'CZ3', 'CH2'],
  'X': ['N', 'CA', 'C', 'CB', 'O'],  // Unknown amino acid
  'U': ['N', 'CA', 'C', 'CB', 'O', 'SE'],  // Selenocysteine
  'Z': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD', 'OE1', 'OE2'],
  'B': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'OD1', 'ND2'],
  'O': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD', 'CE', 'NZ'],
  'J': ['N', 'CA', 'C', 'CB', 'O', 'CG', 'CD1', 'CD2']
};

// Element mapping for common atoms
const ELEMENT_MAP: Record<string, string> = {
  'N': 'N', 'CA': 'C', 'C': 'C', 'CB': 'C', 'O': 'O',
  'CG': 'C', 'CG1': 'C', 'CG2': 'C', 'CD': 'C', 'CD1': 'C', 'CD2': 'C',
  'CE': 'C', 'CE1': 'C', 'CE2': 'C', 'CE3': 'C', 'CZ': 'C', 'CZ2': 'C', 'CZ3': 'C',
  'CH2': 'C', 'NE': 'N', 'NE1': 'N', 'NE2': 'N', 'NH1': 'N', 'NH2': 'N', 'NZ': 'N',
  'ND1': 'N', 'ND2': 'N', 'OD1': 'O', 'OD2': 'O', 'OE1': 'O', 'OE2': 'O',
  'OG': 'O', 'OG1': 'O', 'OH': 'O', 'SG': 'S', 'SD': 'S', 'SE': 'Se'
};

interface Residue {
  aa: string;
  threeLetter: string;
  atoms: Record<string, number[]>;
}

interface JSONInput {
  sequence: string;
  all_atoms?: {
    atom_names: string[];
    atom_coordinates: number[][];
  };
  backbone_coordinates?: {
    N: number[][];
    CA: number[][];
    C: number[][];
  };
}

/**
 * Reconstruct per-residue atom data from flat atom lists
 */
function reconstructResidueAtoms(
  sequence: string,
  atomNames: string[],
  atomCoordinates: number[][]
): Record<number, Residue> {
  const residues: Record<number, Residue> = {};
  let atomIdx = 0;

  for (let resIdx = 0; resIdx < sequence.length; resIdx++) {
    let aa = sequence[resIdx];
    if (aa === '_') {
      aa = 'X'; // Convert masked positions to unknown
    }

    const residueNum = resIdx + 1;
    residues[residueNum] = {
      aa,
      threeLetter: ONE_TO_THREE[aa] || 'UNK',
      atoms: {}
    };

    // Get expected atoms for this amino acid
    const expectedAtoms = ATOMS[aa] || ATOMS['X'];

    // Extract atoms for this residue
    for (const expectedAtom of expectedAtoms) {
      if (atomIdx < atomNames.length && atomNames[atomIdx] === expectedAtom) {
        const coords = atomCoordinates[atomIdx];
        
        // Skip atoms with masked coordinates
        if (coords[0] !== 0 || coords[1] !== 0 || coords[2] !== 0) {
          residues[residueNum].atoms[expectedAtom] = coords;
        }
        
        atomIdx++;
      }
    }
  }

  return residues;
}

/**
 * Format a number for PDB file (right-aligned in field)
 */
function formatPDBNumber(num: number, width: number, decimals: number): string {
  const formatted = num.toFixed(decimals);
  return formatted.padStart(width);
}

/**
 * Format an atom name for PDB (4 characters, left-aligned for most, centered for single char)
 */
function formatAtomName(atomName: string): string {
  if (atomName.length === 1) {
    // Center single character atom names
    return ` ${atomName}  `;
  } else if (atomName.length === 2) {
    // Left-align 2-character names
    return `${atomName}  `;
  } else if (atomName.length === 3) {
    // Left-align 3-character names
    return `${atomName} `;
  } else {
    // Use as-is for 4 characters
    return atomName.padEnd(4);
  }
}

/**
 * Convert JSON format to PDB format
 */
export function convertJSONToPDB(jsonData: JSONInput, chainId: string = 'A'): string {
  const lines: string[] = [];
  
  // Add header
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { 
    day: '2-digit', 
    month: 'short', 
    year: '2-digit' 
  }).toUpperCase();
  
  lines.push(`HEADER    GENERATED FROM JSON                         ${dateStr}   XXXX              `);
  lines.push('TITLE     PROTEIN STRUCTURE CONVERTED FROM JSON FORMAT                             ');
  
  // Extract sequence and atoms
  const sequence = jsonData.sequence.replace(/_/g, 'X'); // Convert masked to unknown
  
  if (!jsonData.all_atoms) {
    throw new Error("JSON data must contain 'all_atoms' section");
  }
  
  const { atom_names: atomNames, atom_coordinates: atomCoordinates } = jsonData.all_atoms;
  
  if (atomNames.length !== atomCoordinates.length) {
    throw new Error("Mismatch between atom_names and atom_coordinates lengths");
  }
  
  // Reconstruct per-residue data
  const residues = reconstructResidueAtoms(sequence, atomNames, atomCoordinates);
  
  // Write ATOM records
  let atomCounter = 1;
  
  const sortedResNums = Object.keys(residues).map(Number).sort((a, b) => a - b);
  
  for (const resNum of sortedResNums) {
    const residue = residues[resNum];
    const threeLetter = residue.threeLetter;
    
    // Write atoms in standard order
    const expectedAtoms = ATOMS[residue.aa] || ATOMS['X'];
    
    for (const atomName of expectedAtoms) {
      // Skip CB atoms for Glycine residues (Gly should only have N, CA, C, O)
      if (residue.aa === 'G' && atomName === 'CB') {
        continue;
      }
      
      if (atomName in residue.atoms) {
        const coords = residue.atoms[atomName];
        
        // Get element symbol
        const element = ELEMENT_MAP[atomName] || atomName[0];
        
        // Format PDB ATOM line
        // ATOM format: 
        // 1-6: "ATOM  "
        // 7-11: atom serial number (right-aligned)
        // 13-16: atom name (special formatting)
        // 17: alternate location indicator
        // 18-20: residue name
        // 22: chain identifier
        // 23-26: residue sequence number (right-aligned)
        // 27: insertion code
        // 31-38: x coordinate (8.3 format)
        // 39-46: y coordinate (8.3 format)
        // 47-54: z coordinate (8.3 format)
        // 55-60: occupancy (6.2 format)
        // 61-66: temperature factor (6.2 format)
        // 77-78: element symbol (right-aligned)
        
        const atomSerial = atomCounter.toString().padStart(5);
        const atomNameFormatted = formatAtomName(atomName);
        const resNumStr = resNum.toString().padStart(4);
        const x = formatPDBNumber(coords[0], 8, 3);
        const y = formatPDBNumber(coords[1], 8, 3);
        const z = formatPDBNumber(coords[2], 8, 3);
        const elementStr = element.padStart(2);
        
        const atomLine = `ATOM  ${atomSerial} ${atomNameFormatted} ${threeLetter} ${chainId}${resNumStr}    ${x}${y}${z}  1.00 20.00          ${elementStr}  `;
        lines.push(atomLine);
        atomCounter++;
      }
    }
  }
  
  // Add end record
  lines.push('END');
  
  return lines.join('\n');
}

/**
 * Create PDB file from JSON string or object and trigger download
 */
export function downloadPDBFromJSON(
  jsonInput: string | JSONInput,
  filename: string = 'output.pdb',
  chainId: string = 'A'
): void {
  try {
    // Parse JSON if string
    const jsonData: JSONInput = typeof jsonInput === 'string' 
      ? JSON.parse(jsonInput) 
      : jsonInput;
    
    // Convert to PDB format
    const pdbContent = convertJSONToPDB(jsonData, chainId);
    
    // Create blob and download
    const blob = new Blob([pdbContent], { type: 'chemical/x-pdb' });
    const url = URL.createObjectURL(blob);
    
    // Create download link
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    // Clean up
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error converting JSON to PDB:', error);
    throw error;
  }
}